import { styled } from '@mui/material';

import { Icons, Tooltip, Typography, Stack, tokens } from '@cast/design-system';
import { WorkloadResourceConfig } from '@cast/types';

import {
  formatThresholdPercent,
  getRecommendationNoChangeReason,
} from './_utils';

const Icon = styled(Icons.Info)({
  color: tokens.theme.fg.structural.secondary,
  fontSize: 16,
  marginLeft: 4,
  verticalAlign: 'middle',
  cursor: 'help',
});

type Props = {
  requested?: number;
  recommended?: number;
  config: WorkloadResourceConfig | undefined;
};

/**
 * Small info-icon + tooltip explaining why the recommendation does not change
 * the current request: the delta is within the configured apply threshold.
 *
 * Hidden when the reason cannot be derived, when the recommendation indicates a real
 * change (delta above threshold), or when the host (e.g. read-only state) decides not
 * to show it.
 */
export const NoChangeReason = ({ requested, recommended, config }: Props) => {
  const reason = getRecommendationNoChangeReason({
    requested,
    recommended,
    config,
  });

  if (!reason) {
    return null;
  }

  const delta = formatThresholdPercent(reason.deltaPercentage);
  const threshold = formatThresholdPercent(reason.thresholdPercentage);

  return (
    <Tooltip
      title={
        <Stack gap={4} data-testid="no-change-reason-tooltip">
          <Typography variant="P12B">Change too small to apply</Typography>
          <Typography variant="P12R">
            The recommendation is within {delta}% of the current request, below
            the {threshold}% apply threshold, so Cast AI won&apos;t restart the
            workload to apply it.
          </Typography>
          {reason.dynamic && (
            <Typography variant="P10R" color="textSecondary">
              The threshold is dynamic and gets smaller as requests grow.
            </Typography>
          )}
        </Stack>
      }
      placement="top"
      arrow
    >
      <Icon data-testid="no-change-reason-icon" weight="fill" />
    </Tooltip>
  );
};
